import { create } from 'zustand'
import type { SerializedEvent } from '../matrix/events'

interface TimelineState {
  timelines: Map<string, SerializedEvent[]>

  addEvent: (roomId: string, event: SerializedEvent) => void
  prependEvents: (roomId: string, events: SerializedEvent[]) => void
  replaceEvent: (roomId: string, oldEventId: string, newEvent: SerializedEvent) => void
  removeEvent: (roomId: string, eventId: string) => void
  clearRoom: (roomId: string) => void
}

const EMPTY_TIMELINE: SerializedEvent[] = []

export const useTimelineStore = create<TimelineState>((set) => ({
  timelines: new Map(),

  addEvent: (roomId, event) =>
    set((state) => {
      const timelines = new Map(state.timelines)
      const events = [...(timelines.get(roomId) ?? [])]
      const existingIdx = events.findIndex((e) => e.eventId === event.eventId)
      if (existingIdx !== -1) {
        events[existingIdx] = event
      } else {
        const insertIdx = events.findIndex((e) => e.timestamp > event.timestamp)
        if (insertIdx === -1) {
          events.push(event)
        } else {
          events.splice(insertIdx, 0, event)
        }
      }
      timelines.set(roomId, events)
      return { timelines }
    }),

  prependEvents: (roomId, newEvents) =>
    set((state) => {
      const timelines = new Map(state.timelines)
      const existing = timelines.get(roomId) ?? []
      const existingIds = new Set(existing.map((e) => e.eventId))
      const unique = newEvents.filter((e) => !existingIds.has(e.eventId))
      const merged = [...unique, ...existing]
      merged.sort((a, b) => a.timestamp - b.timestamp)
      timelines.set(roomId, merged)
      return { timelines }
    }),

  replaceEvent: (roomId, oldEventId, newEvent) =>
    set((state) => {
      const timelines = new Map(state.timelines)
      const events = timelines.get(roomId)
      if (!events) return state
      const idx = events.findIndex((e) => e.eventId === oldEventId)
      if (idx === -1) return state
      const updated = [...events]
      updated[idx] = newEvent
      timelines.set(roomId, updated)
      return { timelines }
    }),

  removeEvent: (roomId, eventId) =>
    set((state) => {
      const timelines = new Map(state.timelines)
      const events = timelines.get(roomId)
      if (!events) return state
      timelines.set(
        roomId,
        events.filter((e) => e.eventId !== eventId)
      )
      return { timelines }
    }),

  clearRoom: (roomId) =>
    set((state) => {
      const timelines = new Map(state.timelines)
      timelines.delete(roomId)
      return { timelines }
    }),
}))

export function selectTimeline(roomId: string) {
  return (s: TimelineState) => s.timelines.get(roomId) ?? EMPTY_TIMELINE
}
